/**
 * Web Bluetooth driver for the Half Decent Scale and the original Decent Scale. EXPERIMENTAL: written from Decent's
 * published API, never tried on real hardware. Wire format lives in decentProtocol.ts; see
 * docs/decent-scale-protocol.md.
 *
 * Lifecycle: the registry (registry.ts) creates a DecentScale for a device the picker or Chrome's getDevices()
 * returned. connect() opens GATT, checks for the FFF0 service, subscribes to FFF4 and sends LED-on, which starts the
 * ~10 Hz weight stream and opts in to the heartbeat. Later calls (the store's auto-reconnect, "Reconnect") reuse the
 * same BluetoothDevice and only re-open GATT.
 *
 * Writes go through one queue, at least MIN_WRITE_GAP_MS apart: the scale ignores a command that arrives while it
 * is still executing the previous one.
 *
 * Session timers:
 * - Heartbeat: every HEARTBEAT_INTERVAL_MS, or the Half Decent Scale drops the link after 5 s. It stops after
 *   powerOff() and when the session ends.
 * - Liveness watchdog: no frame for STALE_MS reports onStale(true); a much longer silence while the page is visible
 *   drops the link so the store's reconnect path runs.
 *
 * Send-twice: firmware v1.0 drops commands now and then, so commands go out twice until the scale reports a newer
 * firmware. A tare's repeat is skipped once the scale confirmed it.
 *
 * Optional capabilities (types.ts): diagnostics (the packet log summary) and powerOff. No keepAwake (the scale stays
 * on while connected and weighing) and no tareCheck (the scale confirms tares).
 */
import { connectTimeout, describeBleError, getDriverService, pickWriteMode, ScaleServiceMissingError } from './bluetooth';
import {
  DECENT_NAME_PREFIX,
  DECENT_NOTIFY_CHAR_UUID,
  DECENT_SERVICE_UUID,
  DECENT_WRITE_CHAR_UUID,
  decentCommands,
  isDecentScaleName,
  parseDecentFrame,
  type DecentFirmware,
} from './decentProtocol';
import type { BluetoothScaleDriverDescriptor, RawPacketListener, RawWriteListener, ScaleConnectOptions, ScaleDiagnosticsCapability, ScaleDiagnosticsItem, ScaleDriver, ScaleReading } from './types';

const CONNECT_TIMEOUT_MS = 15_000;
const CONNECT_RETRY_DELAY_MS = 600;
const WRITE_RETRY_DELAY_MS = 150;
/** Decent recommends 200 ms between commands. It is also the gap between a command and its repeat (>= 50 ms). */
export const MIN_WRITE_GAP_MS = 200;
/** Well inside the Half Decent Scale's 5 s limit, with room for a late timer or a queued command. */
export const HEARTBEAT_INTERVAL_MS = 2_000;

const WATCHDOG_CHECK_MS = 500;
/** The scale streams at 10 Hz: no frame for this long means the last weight is no longer live. */
export const STALE_MS = 1_500;
/** Silence, counted only while the page is visible, after which the link is dropped to force a reconnect. */
export const STALE_DISCONNECT_MS = 12_000;

/** Registry entry (registry.ts). FFF0 is shared by other scales, so the picker filters by name only. */
export const decentDriver: BluetoothScaleDriverDescriptor = {
  kind: 'decent',
  transport: 'bluetooth',
  label: 'Decent Scale / Half Decent Scale',
  shortName: 'Decent',
  filters: [{ namePrefix: DECENT_NAME_PREFIX }],
  bluefyFilter: { namePrefix: DECENT_NAME_PREFIX },
  optionalServices: [DECENT_SERVICE_UUID],
  primaryServices: [DECENT_SERVICE_UUID],
  matches: isDecentScaleName,
  create: (device) => new DecentScale(device),
  capabilities: ['diagnostics', 'powerOff'],
  experimental: true,
  copy: {
    pickerName: 'Decent Scale',
    vendorApp: 'the Decent app (DE1 tablet)',
    controlsNote: 'The Decent scale doesn’t report its timer to Taretto: the timer buttons only drive the scale’s own display.',
    capabilityNotes: {
      powerOff: 'Decent: the Half Decent Scale, or the Decent Scale with firmware 1.2 or later.',
    },
  },
};

export interface DecentDiagnostics {
  /** Notifications received. */
  packets: number;
  weightPackets: number;
  /** Frames whose last byte isn't the XOR of the others. Counted only: such frames are still used. */
  badChecksum: number;
  /** Shorter than 7 bytes. */
  malformed: number;
  unknown: number;
  buttons: number;
  tareConfirmed: number;
  heartbeatsSent: number;
  /** Repeats actually written (the send-twice quirk). */
  repeatsSent: number;
  firmware: DecentFirmware | null;
  firmwareByte: number | null;
  units: 'g' | 'oz' | null;
  usbPowered: boolean;
  powerOffEvent: boolean;
  writeMode: 'withResponse' | 'withoutResponse' | 'legacy' | null;
  stale: boolean;
}

interface Session {
  server: BluetoothRemoteGATTServer;
  writeChar: BluetoothRemoteGATTCharacteristic;
  timers: Array<ReturnType<typeof setInterval>>;
  cleanup: Array<() => void>;
  lastFrameAt: number;
  /** Last time the page became visible again or the watchdog timer itself was frozen. */
  resumeAt: number;
  lastCheckAt: number;
  stale: boolean;
  /** powerOff() was sent: no more heartbeats. */
  poweredOff: boolean;
  /** A heartbeat is queued or being written: don't pile up more behind a slow write. */
  heartbeatPending: boolean;
}

interface SendOptions {
  /** Send it a second time (the v1.0 quirk). Defaults to whether the firmware needs it. */
  repeat?: boolean;
  /** Checked when the repeat's turn comes: true skips it. */
  skipRepeat?: () => boolean;
}

export class DecentScale implements ScaleDriver {
  readonly kind = 'decent' as const;

  private readonly device: BluetoothDevice;
  private session: Session | null = null;
  private connecting: Promise<void> | null = null;
  /** Incremented to invalidate an in-flight connection attempt (timeout / disconnect()). */
  private attempt = 0;
  private writeChain: Promise<void> = Promise.resolve();
  private lastWriteAt = Number.NEGATIVE_INFINITY;
  private tareCounter = 0;
  /** The counter of the last tare the scale confirmed, since the latest tare was sent. */
  private confirmedTare: number | null = null;
  private batteryPct: number | null = null;
  private readonly readingListeners = new Set<(r: ScaleReading) => void>();
  private readonly disconnectListeners = new Set<(reason?: string) => void>();
  private readonly rawListeners = new Set<RawPacketListener>();
  private readonly writeListeners = new Set<RawWriteListener>();
  private readonly staleListeners = new Set<(stale: boolean) => void>();
  private diag: DecentDiagnostics = emptyDiagnostics();

  readonly diagnostics: ScaleDiagnosticsCapability = {
    summary: () => ({ badge: this.diag.firmware && this.diag.firmware !== 'unknown' ? `Decent ${this.diag.firmware}` : 'Decent', items: diagnosticsItems(this.stats) }),
  };

  constructor(device: BluetoothDevice) {
    this.device = device;
  }

  get name(): string {
    return this.device.name?.trim() || 'Decent scale';
  }

  get connected(): boolean {
    return this.session?.server.connected === true;
  }

  get stats(): DecentDiagnostics {
    return { ...this.diag };
  }

  /**
   * Firmware v1.0 drops commands now and then, so they go out twice until the scale reports something newer. The
   * firmware is kept across reconnects of this instance: the same scale doesn't change firmware in between.
   */
  get repeatsCommands(): boolean {
    return this.diag.firmwareByte === null || this.diag.firmware === 'v1.0';
  }

  connect(options: ScaleConnectOptions = {}): Promise<void> {
    if (this.connected) return Promise.resolve();
    if (this.connecting) return this.connecting;
    this.endSession();
    const attempt = ++this.attempt;
    const p = this.establish(attempt, options.timeoutMs ?? CONNECT_TIMEOUT_MS).finally(() => {
      if (this.connecting === p) this.connecting = null;
    });
    this.connecting = p;
    return p;
  }

  /** Closes the session, or cancels (and aborts) a pending connection attempt. Safe to call repeatedly. */
  async disconnect(): Promise<void> {
    this.attempt++;
    const hadSession = this.session !== null;
    const active = hadSession || this.connecting !== null;
    this.connecting = null;
    this.endSession();
    this.device.removeEventListener('gattserverdisconnected', this.onGattDisconnected);
    if (active) this.device.gatt?.disconnect();
    if (hadSession) this.emitDisconnect();
  }

  /** The scale confirms a tare (a 0F reply), so the repeat is skipped once that arrived. */
  tare(): Promise<void> {
    const counter = (this.tareCounter = (this.tareCounter + 1) & 0xff);
    this.confirmedTare = null;
    return this.send('tare', decentCommands.tare(counter), { skipRepeat: () => this.confirmedTare === counter });
  }

  startTimer(): Promise<void> {
    return this.send('start timer', decentCommands.startTimer());
  }

  stopTimer(): Promise<void> {
    return this.send('stop timer', decentCommands.stopTimer());
  }

  resetTimer(): Promise<void> {
    return this.send('reset timer', decentCommands.resetTimer());
  }

  /** Firmware v1.2 and the Half Decent Scale; older firmware ignores it. The heartbeat stops either way. */
  powerOff(): Promise<void> {
    const result = this.send('switch off', decentCommands.powerOff());
    if (this.session) this.session.poweredOff = true;
    return result;
  }

  onReading(cb: (r: ScaleReading) => void): () => void {
    return subscribe(this.readingListeners, cb);
  }

  onDisconnect(cb: (reason?: string) => void): () => void {
    return subscribe(this.disconnectListeners, cb);
  }

  /** Every notification as received, before parsing. */
  onRawPacket(cb: RawPacketListener): () => void {
    return subscribe(this.rawListeners, cb);
  }

  /** Every write attempt to the scale, with whether it went out. */
  onRawWrite(cb: RawWriteListener): () => void {
    return subscribe(this.writeListeners, cb);
  }

  onStale(cb: (stale: boolean) => void): () => void {
    return subscribe(this.staleListeners, cb);
  }

  // ---------------------------------------------------------------- connection

  private async establish(attempt: number, timeoutMs: number): Promise<void> {
    const device = this.device;
    device.addEventListener('gattserverdisconnected', this.onGattDisconnected);
    const timeout = connectTimeout(timeoutMs);
    try {
      await Promise.race([this.openSessionWithRetry(device, attempt), timeout.promise]);
    } catch (e) {
      if (attempt === this.attempt) {
        this.attempt++;
        this.endSession();
        device.gatt?.disconnect();
      }
      throw describeBleError(e, decentDriver);
    } finally {
      timeout.clear();
    }
  }

  private async openSessionWithRetry(device: BluetoothDevice, attempt: number): Promise<void> {
    try {
      await this.openSession(device, attempt);
    } catch (e) {
      // Android's BLE stack regularly fails the first GATT connect "for unknown reason"; one retry fixes most.
      const name = e instanceof Error ? e.name : '';
      if (attempt !== this.attempt || e instanceof ScaleServiceMissingError || name === 'NotFoundError' || name === 'SecurityError') throw e;
      await delay(CONNECT_RETRY_DELAY_MS);
      this.assertCurrent(attempt);
      await this.openSession(device, attempt);
    }
  }

  private async openSession(device: BluetoothDevice, attempt: number): Promise<void> {
    const gatt = device.gatt;
    if (!gatt) throw new Error('This Bluetooth device has no GATT server.');
    const server = gatt.connected ? gatt : await gatt.connect();
    this.assertCurrent(attempt);
    // The service check: FFF0 is shared with other scales, so this only rules out devices that aren't FFF0 at all.
    const service = await getDriverService(server, decentDriver);
    this.assertCurrent(attempt);
    const notifyChar = await service.getCharacteristic(DECENT_NOTIFY_CHAR_UUID);
    const writeChar = await service.getCharacteristic(DECENT_WRITE_CHAR_UUID);
    this.assertCurrent(attempt);

    const cleanup: Array<() => void> = [];
    // A listener per attempt: a superseded attempt's cleanup must not remove a newer session's listener.
    const onValue = (ev: Event) => this.onNotification(ev, notifyChar);
    notifyChar.addEventListener('characteristicvaluechanged', onValue);
    cleanup.push(() => notifyChar.removeEventListener('characteristicvaluechanged', onValue));
    try {
      await notifyChar.startNotifications();
      this.assertCurrent(attempt);
    } catch (e) {
      cleanup.forEach((fn) => fn());
      throw e;
    }

    const now = performance.now();
    // Counters restart per session; the firmware is kept (see repeatsCommands).
    this.diag = { ...emptyDiagnostics(), firmware: this.diag.firmware, firmwareByte: this.diag.firmwareByte, writeMode: pickWriteMode(writeChar) };
    const session: Session = {
      server,
      writeChar,
      timers: [],
      cleanup,
      lastFrameAt: now,
      resumeAt: now,
      lastCheckAt: now,
      stale: false,
      poweredOff: false,
      heartbeatPending: false,
    };
    this.session = session;
    this.startWatchdog(session);
    session.timers.push(setInterval(() => this.heartbeat(session), HEARTBEAT_INTERVAL_MS));
    // The scale streams nothing until an app writes to it. LED-on also opts in to the heartbeat and reports the
    // battery and firmware. Not awaited: a failure shows up as a stale link, and the watchdog takes it from there.
    void this.send('wake', decentCommands.ledOn()).catch(() => undefined);
  }

  private assertCurrent(attempt: number): void {
    if (attempt !== this.attempt) throw new Error('Connection attempt was cancelled.');
  }

  private endSession(): void {
    const s = this.session;
    if (!s) return;
    this.session = null;
    s.timers.forEach((t) => clearInterval(t));
    s.cleanup.forEach((fn) => fn());
  }

  /** Only reported for established sessions: failures while connecting reject connect() instead. */
  private readonly onGattDisconnected = (): void => {
    if (!this.session) return;
    this.endSession();
    this.emitDisconnect('Lost connection to the scale.');
  };

  private emitDisconnect(reason?: string): void {
    for (const cb of [...this.disconnectListeners]) safeCall(() => cb(reason));
  }

  // ---------------------------------------------------------------- heartbeat

  private heartbeat(s: Session): void {
    if (this.session !== s || s.poweredOff || s.heartbeatPending) return;
    s.heartbeatPending = true;
    this.diag.heartbeatsSent++;
    // A failed heartbeat is retried by the next one; if the scale drops the link, the store reconnects.
    void this.send('heartbeat', decentCommands.heartbeat(), { repeat: false })
      .catch(() => undefined)
      .finally(() => {
        s.heartbeatPending = false;
      });
  }

  // ---------------------------------------------------------------- liveness watchdog

  private startWatchdog(s: Session): void {
    s.timers.push(setInterval(() => this.checkLiveness(s), WATCHDOG_CHECK_MS));
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;
    // A page that was hidden (and possibly frozen) restarts the silence clock instead of blaming the scale.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') return;
      const now = performance.now();
      s.resumeAt = now;
      s.lastCheckAt = now;
    };
    document.addEventListener('visibilitychange', onVisibility);
    s.cleanup.push(() => document.removeEventListener('visibilitychange', onVisibility));
  }

  private checkLiveness(s: Session): void {
    if (this.session !== s) return;
    const now = performance.now();
    const sinceLastCheck = now - s.lastCheckAt;
    s.lastCheckAt = now;
    if (!pageVisible()) return;
    if (sinceLastCheck > WATCHDOG_CHECK_MS * 4) {
      // The timer itself was frozen or throttled (background tab, device sleep): our clock is unreliable.
      s.resumeAt = now;
      return;
    }
    const silentMs = now - Math.max(s.lastFrameAt, s.resumeAt);
    if (silentMs >= STALE_DISCONNECT_MS) {
      this.forceDisconnect('The scale stopped sending data.');
      return;
    }
    if (!s.stale && silentMs >= STALE_MS) {
      s.stale = true;
      this.diag.stale = true;
      this.emitStale(true);
    }
  }

  private noteAlive(s: Session, t: number): void {
    s.lastFrameAt = t;
    if (s.stale) {
      s.stale = false;
      this.diag.stale = false;
      this.emitStale(false);
    }
  }

  private forceDisconnect(reason: string): void {
    if (!this.session) return;
    this.endSession();
    this.device.gatt?.disconnect();
    this.emitDisconnect(reason);
  }

  private emitStale(stale: boolean): void {
    for (const cb of [...this.staleListeners]) safeCall(() => cb(stale));
  }

  // ---------------------------------------------------------------- incoming data

  private onNotification(ev: Event, ch: BluetoothRemoteGATTCharacteristic): void {
    // Bluefy dispatches plain objects rather than real Events; fall back to the characteristic itself.
    const value = (ev?.target as BluetoothRemoteGATTCharacteristic | null | undefined)?.value ?? ch.value;
    if (!value) return;
    const t = performance.now();
    if (this.session) this.noteAlive(this.session, t);
    // Copy: Chrome may reuse the underlying buffer, and raw listeners may keep the bytes.
    const bytes = new Uint8Array(value.byteLength);
    bytes.set(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
    const d = this.diag;
    d.packets++;
    for (const cb of [...this.rawListeners]) safeCall(() => cb(bytes, t));

    const result = parseDecentFrame(bytes);
    if (!result.ok) {
      d.malformed++;
      return;
    }
    const frame = result.frame;
    if (!frame.checksumOk) d.badChecksum++;
    switch (frame.type) {
      case 'weight': {
        d.weightPackets++;
        const reading: ScaleReading = { t, weightG: frame.weightG, flowGs: null, timerMs: null, batteryPct: this.batteryPct };
        for (const cb of [...this.readingListeners]) safeCall(() => cb(reading));
        break;
      }
      case 'status':
        this.batteryPct = frame.batteryPct;
        d.usbPowered = frame.usbPowered;
        d.units = frame.units;
        d.firmware = frame.firmware;
        d.firmwareByte = frame.firmwareByte;
        break;
      case 'tare':
        if (frame.confirmed) {
          d.tareConfirmed++;
          this.confirmedTare = frame.counter;
        }
        break;
      case 'button':
        d.buttons++;
        break;
      case 'powerOffEvent':
        d.powerOffEvent = true;
        break;
      case 'batteryVoltage':
        break;
      case 'unknown':
        d.unknown++;
        break;
    }
  }

  // ---------------------------------------------------------------- commands

  /**
   * Queue a command (and, while the firmware needs it, its repeat) behind any in-flight write, each at least
   * MIN_WRITE_GAP_MS after the previous one. Resolves once the first copy is written; the repeat's failure is
   * ignored (the scale may have switched off by then).
   */
  private send(label: string, frame: Uint8Array<ArrayBuffer>, opts: SendOptions = {}): Promise<void> {
    if (!this.connected) return Promise.reject(commandError(label, new Error('Scale not connected')));
    const first = this.enqueue(label, frame);
    if (opts.repeat ?? this.repeatsCommands) {
      void this.enqueue(label, frame, () => {
        if (opts.skipRepeat?.()) return false;
        this.diag.repeatsSent++;
        return true;
      }).catch(() => undefined);
    }
    return first;
  }

  /** `proceed` runs right before the write, after its spacing wait; false skips it. */
  private enqueue(label: string, frame: Uint8Array<ArrayBuffer>, proceed?: () => boolean): Promise<void> {
    const run = () => this.writeSpaced(label, frame, proceed);
    const result = this.writeChain.then(run, run);
    this.writeChain = result.catch(() => undefined);
    return result;
  }

  private async writeSpaced(label: string, frame: Uint8Array<ArrayBuffer>, proceed?: () => boolean): Promise<void> {
    const wait = this.lastWriteAt + MIN_WRITE_GAP_MS - performance.now();
    if (wait > 0) await delay(wait);
    if (proceed && !proceed()) return;
    if (!this.connected) throw commandError(label, new Error('Scale not connected'));
    try {
      await this.writeWithRetry(label, frame);
    } finally {
      this.lastWriteAt = performance.now();
    }
  }

  private async writeWithRetry(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    try {
      await this.writeAttempt(label, frame);
    } catch (first) {
      if (!this.connected) throw commandError(label, first);
      await delay(WRITE_RETRY_DELAY_MS);
      try {
        await this.writeAttempt(label, frame);
      } catch (second) {
        throw commandError(label, second);
      }
    }
  }

  /** One write, reported to the onRawWrite listeners whether it went out or not. */
  private async writeAttempt(label: string, frame: Uint8Array<ArrayBuffer>): Promise<void> {
    let ok = false;
    try {
      await this.writeOnce(frame);
      ok = true;
    } finally {
      const t = performance.now();
      for (const cb of [...this.writeListeners]) safeCall(() => cb(frame, label, t, ok));
    }
  }

  private async writeOnce(frame: Uint8Array<ArrayBuffer>): Promise<void> {
    const s = this.session;
    if (!s || !s.server.connected) throw new Error('Scale not connected');
    const ch = s.writeChar;
    // A fresh buffer the size of the frame: Bluefy has been reported to send a view's whole underlying buffer.
    const bytes = frame.slice();
    switch (pickWriteMode(ch)) {
      case 'withResponse':
        return ch.writeValueWithResponse(bytes);
      case 'withoutResponse':
        return ch.writeValueWithoutResponse(bytes);
      case 'legacy':
        return ch.writeValue(bytes);
    }
  }
}

// ---------------------------------------------------------------- helpers

function emptyDiagnostics(): DecentDiagnostics {
  return {
    packets: 0,
    weightPackets: 0,
    badChecksum: 0,
    malformed: 0,
    unknown: 0,
    buttons: 0,
    tareConfirmed: 0,
    heartbeatsSent: 0,
    repeatsSent: 0,
    firmware: null,
    firmwareByte: null,
    units: null,
    usbPowered: false,
    powerOffEvent: false,
    writeMode: null,
    stale: false,
  };
}

/** The packet log's diagnostics line. */
function diagnosticsItems(d: DecentDiagnostics): ScaleDiagnosticsItem[] {
  const items: ScaleDiagnosticsItem[] = [
    { text: `${d.packets} frames` },
    { text: `${d.weightPackets} weight` },
    { text: `${d.badChecksum} checksum mismatch (still used)` },
    { text: `${d.malformed} malformed`, tone: d.malformed > 0 ? 'warning' : undefined },
    { text: `${d.unknown} unknown` },
    { text: `${d.tareConfirmed} tares confirmed` },
    { text: `${d.heartbeatsSent} heartbeats` },
  ];
  if (d.firmwareByte === null) items.push({ text: 'firmware: not reported', tone: 'warning' });
  else items.push({ text: `firmware: ${d.firmware === 'unknown' ? `0x${d.firmwareByte.toString(16).padStart(2, '0')}` : d.firmware}` });
  items.push({ text: d.firmwareByte === null || d.firmware === 'v1.0' ? `commands sent twice (${d.repeatsSent} repeats)` : 'commands sent once' });
  if (d.usbPowered) items.push({ text: 'USB power' });
  if (d.units === 'oz') items.push({ text: 'display in ounces', tone: 'warning' });
  if (d.writeMode) items.push({ text: `writes: ${d.writeMode}` });
  if (d.buttons > 0) items.push({ text: `${d.buttons} button presses` });
  if (d.powerOffEvent) items.push({ text: 'scale reported power-off' });
  if (d.stale) items.push({ text: 'no data (stale)', tone: 'danger' });
  return items;
}

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function commandError(label: string, cause: unknown): Error {
  const msg = cause instanceof Error ? cause.message : String(cause);
  return new Error(`Couldn't send "${label}" to the scale: ${msg}`, { cause });
}

function subscribe<T>(set: Set<T>, cb: T): () => void {
  set.add(cb);
  return () => {
    set.delete(cb);
  };
}

function safeCall(fn: () => void): void {
  try {
    fn();
  } catch (e) {
    console.error('Decent scale listener failed', e);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
